import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../../../utils/authHeader";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../misc/Select";
import {
    googleFontsHref,
    parseSlideshowFont,
} from "../../../utils/slideshowFont";

interface FontSelectorProps {
    selectedFont: string;
    setSelectedFont: (font: string) => void;
}

// Default font family. The video service's GET /slideshow/fonts now serves
// font *family* names (e.g. "Inter") and resolves the weight per template, so
// the value sent must be a family, not a "<Family>-<Weight>.ttf" file name.
const DEFAULT_FONT = "Inter";

const FontSelector: React.FC<FontSelectorProps> = ({
    selectedFont,
    setSelectedFont,
}) => {
    // Start empty + loading so no placeholder fonts flash before the API
    // resolves (the list would otherwise visibly change once it returns).
    const [fonts, setFonts] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFonts = async (): Promise<void> => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await axios.get<
                    string[] | { fonts?: string[] }
                >(`${backendUrl}/listing/slideshow/fonts`, { headers });
                // The video micro-service may return a bare array or wrap it in
                // a `fonts` field — accept either.
                const fontList = Array.isArray(response.data)
                    ? response.data
                    : response.data?.fonts;
                if (Array.isArray(fontList) && fontList.length > 0) {
                    setFonts(fontList);
                    // Only replace a selection the service can't render: the
                    // caller's font is a real preference (seeded from Listing
                    // Video Settings, or picked by the user) and overwriting a
                    // valid one silently discards it. Falling back, prefer the
                    // default, else the first available font.
                    if (!fontList.includes(selectedFont)) {
                        setSelectedFont(
                            fontList.includes(DEFAULT_FONT)
                                ? DEFAULT_FONT
                                : fontList[0]
                        );
                    }
                } else {
                    setFonts([DEFAULT_FONT]);
                    setSelectedFont(DEFAULT_FONT);
                }
            } catch {
                // Endpoint unavailable — offer only the default font (it's
                // guaranteed to ship in the video service), no arbitrary list.
                setFonts([DEFAULT_FONT]);
                setSelectedFont(DEFAULT_FONT);
            } finally {
                setLoading(false);
            }
        };
        fetchFonts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load each distinct Google Font once so dropdown options render in their
    // real typeface (mirrors the lead-gen PostGraphicsEditor loader). The
    // injected <link>s are intentionally left in <head> on unmount — removing
    // them would flash a fallback font on remount, and the `gf-` id guard makes
    // re-injection idempotent. Stylesheets are browser-cached, so the repeat
    // fetch is cheap.
    useEffect(() => {
        const families = new Set(
            fonts
                .map((font) => parseSlideshowFont(font).googleFamily)
                .filter((family): family is string => family !== null),
        );
        families.forEach((family) => {
            const id = `gf-${family.replace(/\s/g, "-")}`;
            if (document.getElementById(id)) return;
            const link = document.createElement("link");
            link.id = id;
            link.rel = "stylesheet";
            link.href = googleFontsHref(family);
            document.head.appendChild(link);
        });
    }, [fonts]);

    return (
        <div className="space-y-2">
            <h4 className="text-base font-semibold text-primary">Font</h4>
            <p className="text-sm text-neutral-500">
                The font used for the text in your slideshow.
            </p>
            <Select
                value={selectedFont}
                onValueChange={setSelectedFont}
                disabled={loading}
            >
                <SelectTrigger className="w-full sxl:w-72">
                    <SelectValue
                        placeholder={
                            loading ? "Loading fonts…" : "Select a font"
                        }
                    />
                </SelectTrigger>
                <SelectContent>
                    {fonts.map((font) => {
                        const meta = parseSlideshowFont(font);
                        return (
                            <SelectItem key={font} value={font}>
                                <span
                                    style={{
                                        fontFamily: meta.cssFamily,
                                        // Preview each option in its own face at
                                        // Regular weight.
                                        fontWeight: 400,
                                        fontStyle: "normal",
                                    }}
                                >
                                    {meta.label}
                                </span>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
        </div>
    );
};

export default FontSelector;
